import { Style } from 'mapbox-gl';
import { combineReducers } from 'redux';
import { ActionType, getType } from 'typesafe-actions';

import Agreement from '../models/Agreement';
import LaserficheCredentials from '../models/LaserficheCredentials';
import Layer, { LayerBounds } from '../models/Layer';
import * as actions from './actions';
import initialState from './initial-state';

export type Action = ActionType<typeof actions>;

export interface State {
  readonly agreements: Record<string, Agreement>;
  readonly agreementsPopupTab: number;
  readonly agreementsTable: { order: 'asc' | 'desc'; orderBy: string };
  readonly basemapStyles: Style[];
  readonly companyOptions: string[];
  readonly documentsRequested: string[];
  readonly identify: {
    /** All features in the current identify (from last identify map click) */
    entities: {
      [featureId: string]: {
        id: string;
        properties: any;
        layer: { id: string };
        [key: string]: any;
      };
    };
    /** Latitude/longitude location of the last identify map click */
    location: { lng: number; lat: number } | null;
    /**
     * Id of the selected feature from the current identify set.
     * Selected features are the single visible feature displaying props in the map popup.
     */
    selectedId: string | null;
  };
  readonly isAgreementsTableOpen: boolean;
  readonly isPopupLoading: boolean;
  readonly laserficheCredentials: LaserficheCredentials;
  readonly mapLayerBounds: LayerBounds;
  readonly map: { center: [number, number]; zoom: number };
  readonly mapPopupLayers: string[];
  readonly mapStyleBase: Style;
  readonly searchFields: any[];
  readonly selectedAgreementId: string | null;
  readonly selectedTableAgreementMapLocation: {
    popupAnchor: [number, number] | null;
    bounds: [number, number, number, number] | null;
  };
  readonly sidebarTab: number;
  readonly signInDialog: { open: boolean; errorMessage: string; isLoading: boolean };
  readonly title: string;
  readonly todoActions: string[];
  readonly toggleableLayers: Layer[];
}

export default combineReducers<State, Action>({
  agreements: (state = initialState.agreements, action) => {
    switch (action.type) {
      case getType(actions.receiveAgreements):
        return action.payload;
      case getType(actions.receiveDocuments):
        const newSuccessState = { ...state };
        newSuccessState[action.payload.agreementId] = {
          ...state[action.payload.agreementId],
          fetchedDocuments: action.payload.documents,
          hasFetched: true,
          isLoading: false
        };
        return newSuccessState;
      case getType(actions.errorFetchingDocuments):
        const newFailedState = { ...state };
        newFailedState[action.payload.agreementId] = {
          ...state[action.payload.agreementId],
          errorMessage: action.payload.errorMessage,
          hasFetched: false,
          isLoading: false
        };
        return newFailedState;
      case getType(actions.setDocumentsLoading):
        const newLoadingState = { ...state };
        newLoadingState[action.payload.agreementId] = {
          ...state[action.payload.agreementId],
          isLoading: action.payload.isLoading
        };
        return newLoadingState;
      case getType(actions.signOutUser):
        return initialState.agreements;

      default:
        return state;
    }
  },
  agreementsPopupTab: (state = initialState.agreementsPopupTab, action) => {
    switch (action.type) {
      case getType(actions.setAgreementsPopupTab):
        return action.payload;
      default:
        return state;
    }
  },
  agreementsTable: (state = initialState.agreementsTable, action) => {
    switch (action.type) {
      case getType(actions.setAgreementsTableOrder):
        return { order: action.payload, orderBy: action.meta };

      default:
        return state;
    }
  },
  basemapStyles: (state = initialState.basemapStyles) => state,
  companyOptions: (state = initialState.companyOptions, action) => {
    switch (action.type) {
      case getType(actions.receiveAgreements):
        const allCompanies = Object.keys(action.payload).reduce((acc, agreementId) => {
          if (acc[action.payload[agreementId].companyName]) {
            return acc;
          }
          acc[action.payload[agreementId].companyName] = null;
          return acc;
        }, {});
        return Object.keys(allCompanies);

      default:
        return state;
    }
  },
  documentsRequested: (state = initialState.documentsRequested, action) => {
    switch (action.type) {
      case getType(actions.addDocumentToTodoActions):
        return [...state, action.payload];
      case getType(actions.receiveDocuments):
      case getType(actions.errorFetchingDocuments):
        return [...state.slice(1)];
      default:
        return state;
    }
  },
  identify: (state = initialState.identify, action) => {
    switch (action.type) {
      case getType(actions.setIdentifyState):
        const { features, location } = action.payload;
        return {
          ...state,
          location,
          entities: features.reduce((acc, f) => ({ ...acc, [f.id]: f }), {})
        };

      case getType(actions.clearIdentifyState):
        return initialState.identify;

      case getType(actions.setIdentifySelectedId):
        const id = action.payload;
        return { ...state, selectedId: id && state.entities[id] ? id : null };

      default:
        return state;
    }
  },
  isAgreementsTableOpen: (state = initialState.isAgreementsTableOpen, action) => {
    switch (action.type) {
      case getType(actions.toggleAgreementsTableOpen):
        return !state;
      default:
        return state;
    }
  },
  isPopupLoading: (state = initialState.isPopupLoading, action) => {
    switch (action.type) {
      case getType(actions.setPopupLoading):
        return action.payload;

      default:
        return state;
    }
  },
  laserficheCredentials: (state = initialState.laserficheCredentials, action) => {
    switch (action.type) {
      case getType(actions.setLaserficheUsernamePassword):
        const { username, password } = action.payload;
        return { ...state, username, password, initials: username.slice(0, 2).toUpperCase() };
      case getType(actions.signOutUser):
        return initialState.laserficheCredentials;

      default:
        return state;
    }
  },
  mapLayerBounds: (state = initialState.mapLayerBounds, action) => {
    switch (action.type) {
      case getType(actions.setMapLayerBounds):
        return action.payload;
      default:
        return state;
    }
  },
  map: (state = initialState.map) => {
    return state;
  },
  mapPopupLayers: (state = initialState.mapPopupLayers) => state,
  mapStyleBase: (state = initialState.mapStyleBase, action) => {
    switch (action.type) {
      case getType(actions.setMapStyleBase):
        return action.payload;
      default:
        return state;
    }
  },
  searchFields: (state = initialState.searchFields, action) => {
    switch (action.type) {
      case getType(actions.clearSearchFields):
        return [];
      case getType(actions.removeSearchField):
        return state.filter(field => field.id !== action.payload);
      case getType(actions.addSearchField):
        return [...state, action.payload];
      case getType(actions.replaceSearchField):
        const foundIndexToreplace = state.findIndex(search => search.id === action.payload.id);
        if (foundIndexToreplace !== -1) {
          const newState = [...state];
          newState[foundIndexToreplace] = action.payload;
          return newState;
        }
        return state;
      case getType(actions.modifySearchField):
        const found = state.findIndex(search => search.id === action.payload.id);
        if (found !== -1) {
          const newState = [...state];
          newState[found] = { ...newState[found], ...action.payload };
          return newState;
        }
        return state;

      default:
        return state;
    }
  },
  selectedAgreementId: (state = initialState.selectedAgreementId, action) => {
    switch (action.type) {
      case getType(actions.setSelectedAgreementId):
        return action.payload;

      default:
        return state;
    }
  },
  selectedTableAgreementMapLocation: (
    state = initialState.selectedTableAgreementMapLocation,
    action
  ) => {
    switch (action.type) {
      case getType(actions.setSelectedTableAgreement):
        return { ...action.payload };
      case getType(actions.clearSelectedTableAgreement):
        return initialState.selectedTableAgreementMapLocation;

      default:
        return state;
    }
  },
  sidebarTab: (state = initialState.sidebarTab, action) => {
    switch (action.type) {
      case getType(actions.setSidebarTab):
        return action.payload;

      default:
        return state;
    }
  },
  signInDialog: (state = initialState.signInDialog, action) => {
    switch (action.type) {
      case getType(actions.setSignInDialogStatus):
        return { ...state, open: action.payload };
      case getType(actions.setSignInDialogMessage):
        return { ...state, errorMessage: action.payload };
      case getType(actions.signOutUser):
        return { ...initialState.signInDialog, open: true };

      default:
        return state;
    }
  },
  title: (state = initialState.title, action) => {
    switch (action.type) {
      case getType(actions.setTitle):
        return action.payload;

      default:
        return state;
    }
  },
  todoActions: (state = initialState.todoActions, action) => {
    switch (action.type) {
      case getType(actions.completeTodoAction):
      case getType(actions.receiveDocuments):
      case getType(actions.errorFetchingDocuments):
        return [...state.slice(1)];
      case getType(actions.addDocumentToTodoActions):
        return [...state, 'fetchDocuments'];
      case getType(actions.signOutUser):
        return initialState.todoActions;

      default:
        return state;
    }
  },
  toggleableLayers: (state = initialState.toggleableLayers, action) => {
    switch (action.type) {
      case getType(actions.setToggleableLayer):
        return state.map(layer => ({
          ...layer,
          isChecked: layer.key === action.payload ? !layer.isChecked : layer.isChecked
        }));

      default:
        return state;
    }
  }
});
